import { BaseService } from './base.service';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import { 
  TrainingEnrollmentDto, 
  CreateTrainingEnrollmentDto, 
  UpdateTrainingEnrollmentDto,
  BatchEnrollmentDto 
} from '../types/dtos/training';
import { api } from '../client/axiosClient';

/**
 * TrainingEnrollmentService handles CRUD operations for training enrollments.
 *
 * URL Pattern: /api/v1/training/enrollments
 *               /api/v1/trainings/{trainingId}/enrollments
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * VAL-FE-006: Training Service Works with Header-Based Auth
 * The axiosClient automatically includes X-Organization-Id header from localStorage,
 * which the backend uses for tenant context validation via TenantContextHolder.
 *
 * VAL-TRN-006: Training Enrollment List with X-Organization-Id Header
 *
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param trainingId - Optional training UUID for training-specific enrollments
 */
export class TrainingEnrollmentService extends BaseService<
  TrainingEnrollmentDto,
  CreateTrainingEnrollmentDto,
  UpdateTrainingEnrollmentDto
> {
  private organizationId: string;
  private trainingId?: string;

  constructor(organizationId: string, trainingId?: string) {
    const baseUrl = trainingId 
      ? `/api/v1/trainings/${trainingId}/enrollments`
      : `/api/v1/training/enrollments`;
    super(baseUrl);
    this.organizationId = organizationId;
    this.trainingId = trainingId;
  }

  findAll(params?: PaginationParams): Promise<CustomResponseEntity<TrainingEnrollmentDto[]>> {
    const finalParams = this.organizationId
      ? { ...params, organizationId: this.organizationId }
      : params;
    return api.get(this.baseUrl, { params: finalParams });
  }

  // Batch enroll multiple organizations to a training
  batchEnroll(data: BatchEnrollmentDto): Promise<CustomResponseEntity<TrainingEnrollmentDto[]>> {
    if (!this.trainingId) {
      throw new Error('Training ID is required for batch enrollment');
    }
    return api.post(`${this.baseUrl}/batch`, data);
  }

  // Batch enroll all organizations in a group to a training
  batchEnrollByGroup(groupId: string): Promise<CustomResponseEntity<TrainingEnrollmentDto[]>> {
    if (!this.trainingId) {
      throw new Error('Training ID is required for batch enrollment by group');
    }
    return api.post(`${this.baseUrl}/batch/group/${groupId}`);
  }
}

/**
 * Factory function to create a TrainingEnrollmentService instance
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param trainingId - Optional training UUID for training-specific enrollments
 */
export const createTrainingEnrollmentService = (organizationId: string, trainingId?: string) => 
  new TrainingEnrollmentService(organizationId, trainingId);

